import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import { applyApiFieldErrors } from '@/lib/form-errors';
import { destinationCreateSchema, type Destination, type DestinationCreateInput } from './types';
import {
  useCreateDestination,
  useProposeDestinationChange,
  useUpdateDestinationDirect,
} from './useDestinations';

const DEST_TYPE_OPTIONS = ['buyer', 'secondary_processor'] as const;

export type DestinationsDialogMode = 'direct' | 'proposal';

interface DestinationsFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row?: Destination | null;
  mode?: DestinationsDialogMode;
}

export const DestinationsFormDialog = ({
  open,
  onOpenChange,
  row,
  mode = 'direct',
}: DestinationsFormDialogProps) => {
  const { t } = useTranslation();
  const create = useCreateDestination();
  const updateDirect = useUpdateDestinationDirect();
  const propose = useProposeDestinationChange();
  const isEdit = !!row;
  const isProposal = isEdit && mode === 'proposal';

  // Destinations register by name only — no counterparty picker (完全脱鉤,
  // 2026-07-30). Editing keeps the identity it already has, so the name is
  // only required when creating. superRefine keeps the inferred type stable
  // across modes.
  const baseSchema = destinationCreateSchema
    .extend({
      display_name: z.string().optional(),
      reason: z.string().optional(),
    })
    .superRefine((value, ctx) => {
      if (!isEdit && !value.display_name?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['display_name'],
          message: t('efs.masters.destination.name_required', {
            ns: 'masters',
            defaultValue: '出荷先名を入力してください',
          }),
        });
      }
      if (isProposal && !value.reason?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['reason'],
          message: t('efs.masters.form.reason_required', { ns: 'masters' }),
        });
      }
    });

  type FormValues = Omit<DestinationCreateInput, 'display_name'> & {
    display_name?: string;
    reason?: string;
  };

  const defaults: FormValues = {
    display_name: '',
    legal_name: '',
    destination_type: 'buyer',
    certificate_expires_at: '',
    reason: '',
  };

  const form = useForm<FormValues>({
    resolver: zodResolver(baseSchema),
    defaultValues: defaults,
  });

  useEffect(() => {
    if (!open) {
      form.reset(defaults);
      return;
    }
    if (row) {
      form.reset({
        display_name: '',
        legal_name: '',
        destination_type: row.destination_type,
        certificate_expires_at: row.certificate_expires_at ?? '',
        reason: '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      if (isEdit && row) {
        // Identity is fixed after creation; only the mutable fields travel.
        const payload = {
          destination_type: values.destination_type,
          certificate_expires_at: values.certificate_expires_at || undefined,
        };
        if (isProposal) {
          await propose.mutateAsync({
            id: row.destination_company_id,
            body: { proposed: payload, reason: values.reason ?? '' },
          });
        } else {
          await updateDirect.mutateAsync({
            id: row.destination_company_id,
            body: payload,
          });
        }
      } else {
        await create.mutateAsync({
          display_name: (values.display_name ?? '').trim(),
          legal_name: values.legal_name?.trim() || undefined,
          destination_type: values.destination_type,
          certificate_expires_at: values.certificate_expires_at || undefined,
        });
      }
    } catch (error) {
      // Keep the sheet open and show why — silent failures here were being
      // reported as "登録できない" (2026-07-30).
      applyApiFieldErrors(form, error, [
        'display_name',
        'legal_name',
        'destination_type',
        'certificate_expires_at',
        'reason',
      ]);
      return;
    }
    onOpenChange(false);
  });

  const isPending = create.isPending || updateDirect.isPending || propose.isPending;

  const title = !isEdit
    ? t('efs.masters.destination.create', { ns: 'masters' })
    : isProposal
      ? t('efs.masters.form.mode_proposal', { ns: 'masters' })
      : t('efs.masters.form.mode_direct', { ns: 'masters' });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>
            {isEdit
              ? isProposal
                ? t('efs.masters.form.proposal_subtitle', {
                    ns: 'masters',
                    defaultValue: '変更案は承認後に反映されます。修正理由は必須です。',
                  })
                : t('efs.masters.form.direct_subtitle', {
                    ns: 'masters',
                    defaultValue: '管理者直接編集 — 保存と同時に反映されます。',
                  })
              : t('efs.masters.destination.create_subtitle', {
                  ns: 'masters',
                  defaultValue: '出荷先は承認後に有効化されます',
                })}
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
            {isEdit && row ? (
              <div className="space-y-1">
                <p className="text-sm font-medium">
                  {t('efs.masters.fields.display_name', {
                    ns: 'masters',
                    defaultValue: '表示名',
                  })}
                </p>
                <Input value={row.company_display_name} readOnly className="bg-efs-bg-subtle" />
              </div>
            ) : (
              <>
                <FormField
                  control={form.control}
                  name="display_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('efs.masters.fields.display_name', {
                          ns: 'masters',
                          defaultValue: '表示名',
                        })}
                      </FormLabel>
                      <FormControl>
                        <Input {...field} value={field.value ?? ''} autoFocus />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="legal_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('efs.masters.suppliers.fields.legal_name', {
                          ns: 'masters',
                          defaultValue: '法人正式名稱',
                        })}
                      </FormLabel>
                      <FormControl>
                        <Input {...field} value={field.value ?? ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}
            <FormField
              control={form.control}
              name="destination_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.masters.fields.destination_type', { ns: 'masters' })}
                  </FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DEST_TYPE_OPTIONS.map((opt) => (
                          <SelectItem key={opt} value={opt}>
                            {t(`efs.masters.destination_type.${opt}`, {
                              ns: 'masters',
                              defaultValue: opt,
                            })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="certificate_expires_at"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.masters.fields.certificate_expires_at', { ns: 'masters' })}
                  </FormLabel>
                  <FormControl>
                    <DatePicker value={field.value ?? ''} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {isProposal ? (
              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('efs.masters.form.reason', { ns: 'masters' })}</FormLabel>
                    <FormControl>
                      <Textarea
                        rows={3}
                        placeholder={t('efs.masters.form.reason_placeholder', {
                          ns: 'masters',
                        })}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}

            {form.formState.errors.root?.message ? (
              <p className="text-sm font-medium text-destructive">
                {String(form.formState.errors.root.message)}
              </p>
            ) : null}

            <SheetFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {t('efs.common.actions.cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {isEdit
                  ? t('efs.common.actions.save')
                  : t('efs.masters.actions.create', { ns: 'masters' })}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};
